import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { expensasApi } from '../../../lib/api';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';

const fmt = (n) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Foresta usa columna "Part. Locales (%)". En Torre Emilia no se muestra ni se envía.
const FORESTA_ID = 'edif_6034e848d0ed';

export default function TabParticipacion({ edificioId, registerHeader }) {
  const showLocales = edificioId === FORESTA_ID;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    items: [], suma_part_expensas: 0, suma_part_locales: 0, n_cocheras: 0, grupos: [], unidades_sin_grupo: [],
  });
  const [original, setOriginal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await expensasApi.getParticipacion(edificioId);
      setData(r.data);
      setOriginal(JSON.parse(JSON.stringify(r.data)));
    }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [edificioId]);

  const updateUnidad = (unidadId, field, value) => {
    const items = data.items.map((i) =>
      i.unidad_id === unidadId ? { ...i, [field]: value } : i
    );
    const suma = items.reduce((s, i) => s + Number(i.part_expensas || 0), 0);
    const sumaLoc = items.reduce((s, i) => s + Number(i.part_locales || 0), 0);
    const nc = items.filter(i => Number(i.part_cocheras || 0) > 0).length;
    setData({ ...data, items, suma_part_expensas: suma, suma_part_locales: sumaLoc, n_cocheras: nc });
  };

  const handleCancel = () => {
    setData(JSON.parse(JSON.stringify(original)));
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await expensasApi.saveParticipacion(edificioId, data.items.map(i => ({
        unidad_id: i.unidad_id,
        part_expensas: Number(i.part_expensas || 0),
        part_cocheras: Number(i.part_cocheras || 0),
        // Solo enviamos part_locales si el edificio lo usa (Foresta). En Torre Emilia: 0.
        part_locales: showLocales ? Number(i.part_locales || 0) : 0,
      })));
      toast.success('Participación guardada');
      setEditing(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const sumaOk = Math.abs(data.suma_part_expensas - 100) < 0.05;

  // ── Estructura: 1 fila por UNIDAD LIQUIDABLE PRINCIPAL ──
  // Cocheras asociadas (es_cochera_asociada) se pliegan en columna de su oficina.
  // Subsuelo = fila especial. Cocheras sueltas = filas independientes.
  const subsuelo = useMemo(() => {
    const g = (data.grupos || []).find((x) => (x.nombre || '').toLowerCase() === 'subsuelo');
    const uids = g ? (g.unidad_ids || (g.unidades || []).map((u) => u.unidad_id)) : [];
    return { uids: new Set(uids), nombre: g?.nombre || 'Subsuelo' };
  }, [data.grupos]);

  const cocherasByPrincipal = useMemo(() => {
    const m = {};
    (data.items || []).forEach((u) => {
      if (u.es_cochera_asociada && u.unidad_principal_id && !subsuelo.uids.has(u.unidad_id)) {
        (m[u.unidad_principal_id] = m[u.unidad_principal_id] || []).push(u);
      }
    });
    return m;
  }, [data.items, subsuelo]);

  // Filas principales: NO cocheras asociadas y NO miembros de Subsuelo
  const filas = useMemo(
    () => (data.items || []).filter((u) => !u.es_cochera_asociada && !subsuelo.uids.has(u.unidad_id)),
    [data.items, subsuelo]
  );

  const subsueloAgg = useMemo(() => {
    const members = (data.items || []).filter((u) => subsuelo.uids.has(u.unidad_id));
    const cocheras = members.filter((u) => (u.tipo || '').toLowerCase() === 'cochera');
    return {
      part_expensas: members.reduce((s, u) => s + Number(u.part_expensas || 0), 0),
      part_cocheras: members.reduce((s, u) => s + Number(u.part_cocheras || 0), 0),
      nombres: cocheras.map((u) => u.nombre_unidad),
      n: members.length,
      nCocheras: cocheras.length,
    };
  }, [data.items, subsuelo]);

  const colSpan = showLocales ? 7 : 6;

  useEffect(() => {
    if (!registerHeader) return;
    registerHeader(
      <>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {filas.length} unidades liquidables · Σ Part.Exp{' '}
          <span className={sumaOk ? 'text-emerald-600 font-semibold' : 'text-orange-600 font-semibold'}>
            {fmt(data.suma_part_expensas)}%
          </span>
          {showLocales && (
            <>
              {' · Σ Part.Locales '}
              <span
                className={Math.abs(Number(data.suma_part_locales || 0) - 100) < 0.05 || Number(data.suma_part_locales || 0) === 0
                  ? 'text-slate-600 font-semibold'
                  : 'text-orange-600 font-semibold'}
                data-testid="suma-part-locales"
              >
                {fmt(data.suma_part_locales)}%
              </span>
            </>
          )}
          {' · '}{data.n_cocheras} cocheras
        </span>
        {editing ? (
          <>
            <Button size="sm" variant="outline" onClick={handleCancel} disabled={saving} data-testid="cancelar-part-btn">
              <X className="w-4 h-4 mr-1" /> Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="guardar-part-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Guardar
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={() => setEditing(true)} data-testid="editar-part-btn">
            <Pencil className="w-4 h-4 mr-1" /> Editar
          </Button>
        )}
      </>
    );
    return () => registerHeader(null);
    // eslint-disable-next-line
  }, [filas.length, data.suma_part_expensas, data.suma_part_locales, data.n_cocheras, saving, editing, showLocales]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const renderFila = (u) => {
    const esCochera = (u.tipo || '').toLowerCase() === 'cochera';
    const asoc = cocherasByPrincipal[u.unidad_id] || [];
    const cocheraLabel = esCochera ? 'Independiente' : (asoc.length ? `Sí (${asoc.length})` : 'No');
    const cocheraNombres = esCochera ? '—' : (asoc.map((c) => c.nombre_unidad).join(', ') || '—');
    const partCoch = esCochera
      ? Number(u.part_cocheras || 0)
      : asoc.reduce((s, c) => s + Number(c.part_cocheras || 0), 0);
    return (
      <tr key={u.unidad_id} className="hover:bg-slate-50/60" data-testid={`part-fila-${u.unidad_id}`}>
        <td className="py-1.5 px-3 text-slate-800 font-medium">{u.nombre_unidad}</td>
        <td className="py-1.5 px-3 text-slate-500 text-xs">{u.tipo}</td>
        <td className="py-1.5 px-3">
          <Input type="number" step="0.0001" className="h-7 text-right w-28 ml-auto"
                 value={u.part_expensas ?? 0} disabled={!editing}
                 data-testid={`part-exp-${u.unidad_id}`}
                 onChange={(e) => updateUnidad(u.unidad_id, 'part_expensas', e.target.value)} />
        </td>
        <td className="py-1.5 px-3 text-center text-xs">
          <span className={esCochera ? 'text-violet-700' : (asoc.length ? 'text-emerald-700' : 'text-slate-400')}>
            {cocheraLabel}
          </span>
        </td>
        <td className="py-1.5 px-3 text-slate-600 text-xs">{cocheraNombres}</td>
        <td className="py-1.5 px-3 text-right font-mono text-slate-700">{partCoch || 0}</td>
        {showLocales && (
          <td className="py-1.5 px-3">
            <Input type="number" step="0.0001" className="h-7 text-right w-28 ml-auto"
                   value={u.part_locales ?? 0} disabled={!editing}
                   data-testid={`part-locales-${u.unidad_id}`}
                   onChange={(e) => updateUnidad(u.unidad_id, 'part_locales', e.target.value)} />
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto h-[calc(100vh-130px)] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500 sticky top-0 z-10">
          <tr>
            <th className="py-2 px-3 text-left font-medium">Unidad</th>
            <th className="py-2 px-3 text-left font-medium">Tipo</th>
            <th className="py-2 px-3 text-right font-medium">Part. Expensas (%)</th>
            <th className="py-2 px-3 text-center font-medium">Cochera</th>
            <th className="py-2 px-3 text-left font-medium">Cocheras asociadas</th>
            <th className="py-2 px-3 text-right font-medium">Part. Cocheras</th>
            {showLocales && <th className="py-2 px-3 text-right font-medium">Part. Locales (%)</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filas.length === 0 && subsueloAgg.n === 0 && (
            <tr><td colSpan={colSpan} className="py-8 text-center text-slate-400">No hay unidades liquidables</td></tr>
          )}
          {filas.map(renderFila)}
          {subsueloAgg.n > 0 && (
            <tr className="bg-indigo-50 border-y border-indigo-200" data-testid="part-fila-subsuelo">
              <td className="py-1.5 px-3 font-semibold text-indigo-900">
                {subsuelo.nombre}
                <span className="ml-2 text-[10px] uppercase text-indigo-600 font-normal">grupo especial · {subsueloAgg.n} unidades</span>
              </td>
              <td className="py-1.5 px-3 text-indigo-700 text-xs">grupo</td>
              <td className="py-1.5 px-3 text-right font-mono font-semibold text-indigo-900">{fmt(subsueloAgg.part_expensas)}%</td>
              <td className="py-1.5 px-3 text-center text-xs text-indigo-700">{subsueloAgg.nCocheras} cocheras</td>
              <td className="py-1.5 px-3 text-indigo-700 text-xs">{subsueloAgg.nombres.join(', ')}</td>
              <td className="py-1.5 px-3 text-right font-mono text-indigo-900">{subsueloAgg.part_cocheras}</td>
              {showLocales && <td></td>}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
